import { FC, useEffect, useState } from "react";
import moment from "moment";
import TourDetails from "../(components)/TourDetails";
import { createPaymentPlanOrder, PaymentPlanOrderType } from "../../../data/paymentPlan/_requests";
import { paymentPlanValidationSchema } from "../../../utils/paymentPlanValidation";
import { initiateTourCheckout } from "../../../data/payment/_requests";

import ButtonPrimary from "shared/Button/ButtonPrimary";
import Checkbox from "shared/Checkbox/Checkbox";
import Select from "shared/Select/Select";
import {
  TourType,
  AdditionalOption,
  TourAvailableDate,
  Tour,
  TourDepartureLocation,
} from "../_models";

// Simple UUID alternative for browser compatibility
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

interface TourBookingPending {
  id: number;
  tour_id: number;
  tour_type_id: number;
  adults: number;
  children: number;
  infants: number;
  total_price: string;
  tour_departure_location_id: number;
  tour_departure_date_id: number;
  tour: Tour;
  tour_type: TourType;
  tour_available_date: TourAvailableDate;
  tour_departure_location: TourDepartureLocation;
  tour_booking_passengers: TourBookingPassenger[];
}

interface TourBookingPassenger {
  additional_options: AdditionalOption[];
}

interface PassengerDetail {
  passengerNumber: number;
  optionsPrice: number;
  options: AdditionalOption[];
}

interface PriceBreakdown {
  basePriceTotal: number;
  tourTypePrice: number;
  departureDatePrice: number;
  departureLocationPrice: number;
  passengerDetails: PassengerDetail[];
  basePrice: number;
}

interface SidebarProps {
  tourBookingPending: any;
  currency: string;
  exchangeRate: number;
  isCreatingCustomer: boolean;
  useFormikContext: any;
  handleTermsSelection: any;
  terms: boolean;
  ErrorMessage: any;
  paymentType?: string;
  onPaymentTypeChange?: (value: string) => void;
}

const Sidebar: FC<SidebarProps> = ({
  tourBookingPending,
  currency,
  exchangeRate,
  isCreatingCustomer,
  useFormikContext,
  terms,
  handleTermsSelection,
  ErrorMessage,
  paymentType: propPaymentType,
  onPaymentTypeChange,
}) => {
  const [surcharge, setSurcharge] = useState<string>("");
  const totalPriceAud = tourBookingPending.total_price;
  const [subTotalPrice, setSubTotalPrice] = useState<string>("");
  const [totalPrice, setTotalPrice] = useState<string>("");
  const [paymentType, setPaymentType] = useState<string>(propPaymentType || "full");
  const [depositAmount, setDepositAmount] = useState<string>("");
  const [depositError, setDepositError] = useState<string>("");
  const [weekOptions, setWeekOptions] = useState<number[]>([2, 4, 6, 8, 12, 16, 24]);
  const [selectedNumberOfWeeks, setSelectedNumberOfWeeks] = useState<number>(2);
  const [isSubmittingPaymentPlan, setIsSubmittingPaymentPlan] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [departureDate, setDepartureDate] = useState<string>(
    tourBookingPending?.tour_available_date?.from_date || ""
  );

  const exchangeMultiplier = currency === "LKR" ? exchangeRate : 1;

  // Sync with prop changes
  useEffect(() => {
    if (propPaymentType) {
      setPaymentType(propPaymentType);
    }
  }, [propPaymentType]);

  // Validate deposit amount
  const validateDepositAmount = (value: string) => {
    const depositValue = parseFloat(value);
    const productPrice = parseFloat(totalPrice);

    if (!value || value.trim() === "") {
      return "Deposit amount is required";
    }

    if (isNaN(depositValue) || depositValue <= 0) {
      return "Deposit amount must be positive";
    }

    if (isNaN(productPrice) || productPrice <= 0) {
      return "Invalid product price";
    }

    // Round values to 2 decimal places to handle floating-point precision
    const roundedDepositValue = Math.round(depositValue * 100) / 100;
    const roundedProductPrice = Math.round(productPrice * 100) / 100;
    const minimumDeposit = Math.round(roundedProductPrice * 0.1 * 100) / 100;
    
    if (roundedDepositValue < minimumDeposit) {
      return "Deposit amount must be at least 10% of the product price";
    }

    if (roundedDepositValue > roundedProductPrice) {
      return "Deposit amount cannot exceed the product price";
    }

    return "";
  };

  // Check if payment plan form is valid
  const isPaymentPlanFormValid = () => {
    // Check required fields
    const hasDepositAmount = depositAmount.trim() !== "";
    const hasValidDeposit = depositError === "" && hasDepositAmount;
    const hasTermsAccepted = terms;

    return hasValidDeposit && hasTermsAccepted;
  };

  const getPriceBreakdown = (
    tourBookingPending: TourBookingPending,
    currency: string,
    exchangeRate: number
  ): PriceBreakdown => {
    const basePrice =
      parseFloat(tourBookingPending.tour.base_price) * exchangeMultiplier;

    const basePriceTotal =
      basePrice *
      (tourBookingPending.adults +
        tourBookingPending.children +
        tourBookingPending.infants);

    let tourTypePrice = 0;
    let departureDatePrice = 0;
    let departureLocationPrice = 0;

    const passengerDetails: PassengerDetail[] =
      tourBookingPending.tour_booking_passengers.map((passenger, index) => {
        let optionsPrice = 0;
        const options = passenger.additional_options.map((option) => {
          const optionPrice =
            Number(option.additional_price) * exchangeMultiplier;
          optionsPrice += optionPrice;
          return {
            id: option.id,
            additional_price: optionPrice,
            option_title: option.option_title,
          };
        });

        return {
          passengerNumber: index + 1,
          optionsPrice,
          options,
        };
      });

    console.log("tourBookingPending :: ", tourBookingPending);

    tourTypePrice =
      parseFloat(tourBookingPending.tour_type.additional_price) *
      exchangeMultiplier *
      (tourBookingPending.adults +
        tourBookingPending.children +
        tourBookingPending.infants);

    departureDatePrice =
      parseFloat(tourBookingPending.tour_available_date.additional_price) *
      exchangeMultiplier *
      (tourBookingPending.adults +
        tourBookingPending.children +
        tourBookingPending.infants);

    departureLocationPrice =
      parseFloat(tourBookingPending.tour_departure_location.additional_price) *
      exchangeMultiplier *
      (tourBookingPending.adults +
        tourBookingPending.children +
        tourBookingPending.infants);

    return {
      basePriceTotal,
      tourTypePrice,
      departureDatePrice,
      departureLocationPrice,
      passengerDetails,
      basePrice,
    };
  };

  const [priceBreakdown, setPriceBreakdown] = useState<PriceBreakdown>(
    getPriceBreakdown(tourBookingPending, currency, exchangeRate)
  );

  const surcharge_percentage_aud: number = parseFloat(
    process.env.REACT_APP_COMMWEB_SURCHARGE || "0"
  );
  const surcharge_percentage_lkr: number = parseFloat(
    process.env.REACT_APP_COMMERCIAL_SURCHARGE || "0"
  );

  useEffect(() => {
    setPriceBreakdown(
      getPriceBreakdown(tourBookingPending, currency, exchangeRate)
    );
    let totalAmount = Number(totalPriceAud);
    if (currency === "LKR") {
      totalAmount = totalPriceAud * exchangeRate;
    }

    let currentSurcharge = 0;
    if (currency === "AUD") {
      currentSurcharge = Number(totalAmount) * (surcharge_percentage_aud / 100);
    } else if (currency === "LKR") {
      currentSurcharge = Number(totalAmount) * (surcharge_percentage_lkr / 100);
    }

    const totalWithSurcharge = Number(totalAmount) + Number(currentSurcharge);

    setSubTotalPrice(totalAmount.toFixed(2));

    setTotalPrice(totalWithSurcharge.toFixed(2));

    setSurcharge(currentSurcharge.toFixed(2));
  }, [
    surcharge_percentage_aud,
    surcharge_percentage_lkr,
    currency,
    exchangeRate,
    totalPriceAud,
  ]);

  const { setFieldValue, values } = useFormikContext();

  // Check if payment plan is enabled for this tour
  const isPaymentPlanEnabled = tourBookingPending?.tour?.enable_payment_plan === 1;

  // Check if departure date is at least 4 weeks from today
  const isDepartureDateValid = () => {
    if (!departureDate) return false;
    const today = moment().startOf('day');
    const departure = moment(departureDate).startOf('day');
    const weeksUntilDeparture = departure.diff(today, 'weeks');
    return weeksUntilDeparture >= 4;
  };

  const canEnablePaymentPlan = isPaymentPlanEnabled && isDepartureDateValid();

  // Auto-switch to full payment if currency changes from AUD while payment plan is selected
  useEffect(() => {
    if (currency !== "AUD" && paymentType === "plan") {
      setPaymentType("full");
      if (onPaymentTypeChange) {
        onPaymentTypeChange("full");
      }
      setFieldValue("paymentType", "full");
    }
  }, [currency, paymentType, onPaymentTypeChange, setFieldValue]);

  // Auto-switch to full payment if payment plan is disabled for this tour
  useEffect(() => {
    if (!canEnablePaymentPlan && paymentType === "plan") {
      setPaymentType("full");
      if (onPaymentTypeChange) {
        onPaymentTypeChange("full");
      }
      setFieldValue("paymentType", "full");
    }
  }, [canEnablePaymentPlan, paymentType, onPaymentTypeChange, setFieldValue]);

  // Handle payment plan submission
  const handlePaymentPlanSubmit = async () => {
    try {
      // Validate required fields
      if (!isPaymentPlanFormValid()) {
        console.error("Payment plan form is not valid");
        return;
      }

      // Get customer email from form values
      const customerEmail = (values as any)?.emailAddress;
      if (!customerEmail) {
        console.error("Customer email is required");
        return;
      }

      // Show spinner
      setIsSubmittingPaymentPlan(true);
      // Prepare payload
      const payload = {
        productName: `tour-prd-${generateId()}`,
        productPrice: Math.round(parseFloat(totalPrice) * 100) / 100,
        customerEmail: customerEmail,
        numberOfWeeks: selectedNumberOfWeeks,
        depositAmount: Math.round(parseFloat(depositAmount) * 100) / 100,
        departureDate: departureDate,
        currency: currency.toLowerCase(), // Convert to lowercase for API
        type: PaymentPlanOrderType.TOUR,
      };

      // Validate payload using Yup schema
      try {
        await paymentPlanValidationSchema.validate(payload, { abortEarly: false });
      } catch (validationError) {
        console.error("Validation failed:", validationError);
        setIsSubmittingPaymentPlan(false);
        return;
      }

      console.log("Submitting payment plan order:", payload);

      // Make API call
      const response = await createPaymentPlanOrder(payload);
      
      if (response.orderId) {
        console.log("Payment plan order created successfully:", response);
        // Wait for 3 seconds to ensure data sync is complete in database tables
        setTimeout(async () => {
          try {
            // Prepare checkout payload
            const checkoutPayload = {
              email_address: customerEmail,
              order_number: tourBookingPending.order_number,
              tour_id: tourBookingPending.tour_id,
              full_name: (values as any)?.customerName || "",
              mobile: (values as any)?.mobile || "",
              adult: (values as any)?.adult || [],
              child: (values as any)?.child || [],
              infant: (values as any)?.infant || [],
              currency: currency,
              terms: terms,
              payment_plan_id: response.orderId,
            };
            
            // Call initiateTourCheckout
            await initiateTourCheckout(checkoutPayload);
            window.location.href = `${window.origin}/payment-instalment?orderId=${response.orderId}&source=web`;
         
          } catch (checkoutError) {
            console.error("Error initiating checkout:", checkoutError);
            setIsSubmittingPaymentPlan(false);
          }
        }, 3000);
      } else {
        console.error("Payment plan order creation failed:", response.message);
        setIsSubmittingPaymentPlan(false);
      }
    } catch (error) {
      console.error("Error submitting payment plan:", error);
      setIsSubmittingPaymentPlan(false);
    }
  };

  // Dynamic week options based on departure date
  useEffect(() => {
    const departureDate = tourBookingPending.tour_available_date.from_date;
    
    if (departureDate) {
      const today = moment().startOf('day');
      const departure = moment(departureDate).startOf('day');
      const diffInWeeks = departure.diff(today, 'weeks');

      // Calculate max weeks: must end 2 weeks before departure
      const maxWeeks = Math.min(20, Math.max(2, diffInWeeks - 2));
      const options = [];

      // Only even numbers between 2 and maxWeeks
      for (let i = 2; i <= maxWeeks; i += 2) {
        options.push(i);
      }

      setWeekOptions(options);

      // If current selection is now invalid, reset it
      if (!options.includes(selectedNumberOfWeeks)) {
        const newValue = options[0] || 2;
        setSelectedNumberOfWeeks(newValue);
        setFieldValue('numberOfWeeks', newValue);
      }
    }
  }, [tourBookingPending.tour_available_date.from_date, selectedNumberOfWeeks, setFieldValue]);

  console.log("priceBreakdown ::: ", priceBreakdown);

  return (
    <div className="w-full flex flex-col sm:rounded-2xl lg:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-8 px-0 sm:p-6 xl:p-8">
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
        {tourBookingPending.tour.tour_title}
      </h1>
      <div className="w-full border-b border-neutral-100 dark:border-neutral-700" />

      <TourDetails tour={tourBookingPending.tour} />

      <div className="flex flex-col space-y-4">
        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
          <span>Number of Passengers</span>
          <span>
            {tourBookingPending.adults +
              tourBookingPending.children +
              tourBookingPending.infants}
          </span>
        </div>
        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
      </div>
      <div className="flex flex-col space-y-4">
        <h3 className="text-2xl font-semibold">Price Details</h3>
        {currency && (
          <div>
            <div>
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300 mb-3">
                <span>
                  Base Price <br />
                  <span
                    className="block sm:inline"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {currency} {priceBreakdown.basePrice} X{" "}
                    {tourBookingPending.adults}
                  </span>
                </span>
                <span>
                  {currency} {priceBreakdown.basePriceTotal.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300 mb-3">
                <span>
                  {tourBookingPending.tour_type.type_title} <br />
                  <span
                    className="block sm:inline"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {currency} {tourBookingPending.tour_type.additional_price * exchangeMultiplier} X{" "}
                    {tourBookingPending.adults}
                  </span>
                </span>
                <span>
                  {currency} {priceBreakdown.tourTypePrice.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300 mb-3">
                <span>
                  Tour Date ({tourBookingPending.tour_available_date.from_date})
                  <br />
                  <span
                    className="block sm:inline"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {currency}{" "}
                    {tourBookingPending.tour_available_date.additional_price * exchangeMultiplier} X{" "}
                    {tourBookingPending.adults}
                  </span>
                </span>
                <span>
                  {currency} {priceBreakdown.departureDatePrice.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300 mb-3">
                <span>
                  Tour Departure Location (
                  {tourBookingPending.tour_departure_location.location_name})
                  <br />
                  <span style={{ fontSize: "0.75rem" }}>
                    {currency}{" "}
                    {tourBookingPending.tour_departure_location
                      .additional_price * exchangeMultiplier}{" "}
                    X {tourBookingPending.adults} Passengers
                  </span>
                </span>
                <span>
                  {currency} {priceBreakdown.departureLocationPrice.toFixed(2)}
                </span>
              </div>

              {priceBreakdown.passengerDetails.map((passengerDetail, index) => (
                <div key={index}>
                  <h4>Passenger {passengerDetail.passengerNumber}</h4>
                  {passengerDetail.options.map((option, optionIndex) => (
                    <div
                      key={optionIndex}
                      className="flex justify-between text-neutral-6000 dark:text-neutral-300 mb-3"
                    >
                      <span>{option.option_title}</span>
                      <span>
                        {currency} {option.additional_price.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex justify-between font-semibold">
              <span>Sub Total</span>
              <span>
                {currency} {subTotalPrice}
              </span>
            </div>
            <div className="flex justify-between font-semibold">
              <span>Surcharge</span>
              <span>
                {currency} {surcharge}
              </span>
            </div>
            <div className="flex justify-between font-semibold">
              <span>Total Price</span>
              <span>
                {currency} {totalPrice}
              </span>
            </div>
          </div>
        )}

        {!currency && (
          <div className="flex justify-between font-semibold">
            <span>Please select the currency.</span>
            <span></span>
          </div>
        )}
      </div>

      <div className="relative flex flex-col  space-y-5">
        <div
          className="bg-blue-200 border-blue-400 text-blue-700 shadow-xl  listingSectionSidebar__wrap"
          role="alert"
        >
          <strong className="font-bold">
            <i className="las la-balance-scale"></i> Terms & Conditions
          </strong>
          <div className="block sm:inline" style={{ fontSize: "0.75rem" }}>
            <a href="/terms-and-conditions" target="_blank">
              Click here to view Terms & Conditions
            </a>
          </div>
        </div>

        {paymentType === "full" && (
          <>
            <Checkbox
              name="terms"
              label="I have read and agree to the conditions"
              checked={terms}
              onChange={(value: boolean) => {
                handleTermsSelection(value);

                setFieldValue("terms", value);
              }}
            />

            <ErrorMessage name="terms" component="div" className="text-red-500" />
          </>
        )}

        <div className="space-y-3">
          <h4 className="text-lg font-semibold">Payment Options</h4>
          
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="paymentType"
                value="full"
                checked={paymentType === "full"}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setPaymentType(selectedValue);
                  if (onPaymentTypeChange) {
                    onPaymentTypeChange(selectedValue);
                  }
                  setFieldValue("paymentType", selectedValue);
                }}
                className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Pay in full online
              </span>
            </label>
            
            <label className={`flex items-center space-x-3 ${
              currency === "AUD" && canEnablePaymentPlan
                ? "cursor-pointer" 
                : "cursor-not-allowed opacity-50"
            }`}>
              <input
                type="radio"
                name="paymentType"
                value="plan"
                checked={paymentType === "plan"}
                disabled={currency !== "AUD" || !canEnablePaymentPlan}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setPaymentType(selectedValue);
                  if (onPaymentTypeChange) {
                    onPaymentTypeChange(selectedValue);
                  }
                  setFieldValue("paymentType", selectedValue);
                }}
                className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Sign up for a payment plan {
                  currency !== "AUD" 
                    ? "(Only available for AUD)" 
                    : !isPaymentPlanEnabled
                    ? "(Not available for this tour)"
                    : !isDepartureDateValid()
                    ? "(Departure must be at least 4 weeks away)"
                    : ""
                }
              </span>
            </label>
          </div>
        </div>
      </div>

{paymentType === "full" ? (
        // Existing payment flow for "Pay in full online"
        <>
          {isCreatingCustomer && (
            <ButtonPrimary loading>In progress...</ButtonPrimary>
          )}
          {!isCreatingCustomer && (
            <ButtonPrimary type="submit">Proceed to Payment</ButtonPrimary>
          )}
        </>
      ) : (
        // Payment plan form for "Sign up for a payment plan"
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">Payment Plan Details</h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Departure Date:
              </label>
              <p className="text-base text-neutral-900 dark:text-neutral-100">
                {departureDate || "N/A"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Product Price:
              </label>
              <p className="text-base text-neutral-900 dark:text-neutral-100">
                ${totalPrice ? Number(totalPrice).toFixed(2) : "0.00"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Deposit Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={parseFloat(totalPrice) * 0.1}
                max={parseFloat(totalPrice)}
                step="0.01"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 ${
                  depositError 
                    ? "border-red-500 dark:border-red-500" 
                    : "border-neutral-300 dark:border-neutral-700"
                }`}
                placeholder="0"
                value={depositAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  setDepositAmount(value);
                  const error = validateDepositAmount(value);
                  setDepositError(error);
                  setFieldValue("depositAmount", value);
                }}
                onBlur={(e) => {
                  const error = validateDepositAmount(e.target.value);
                  setDepositError(error);
                }}
              />
              {depositError && (
                <p className="text-xs text-red-500 mt-1">{depositError}</p>
              )}
              {!depositError && totalPrice && (
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                  Minimum deposit: {currency} {(parseFloat(totalPrice) * 0.1).toFixed(2)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Number of weeks <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedNumberOfWeeks}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setSelectedNumberOfWeeks(value);
                  setFieldValue("numberOfWeeks", value);
                }}
              >
                {weekOptions.map((weeks) => (
                  <option key={weeks} value={weeks}>
                    {weeks} week{weeks > 1 ? 's' : ''}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <Checkbox
            name="terms"
            label="I have read and agree to the conditions"
            checked={terms}
            onChange={(value: boolean) => {
              handleTermsSelection(value);

              setFieldValue("terms", value);
            }}
          />

          <ErrorMessage name="terms" component="div" className="text-red-500" />

          {(isCreatingCustomer || isSubmittingPaymentPlan) && (
            <ButtonPrimary loading>
              {isSubmittingPaymentPlan ? "Processing Payment Plan..." : "Submitting Payment Plan Request..."}
            </ButtonPrimary>
          )}
          {!isCreatingCustomer && !isSubmittingPaymentPlan && (
            <ButtonPrimary 
              type="button"
              onClick={handlePaymentPlanSubmit}
              disabled={!isPaymentPlanFormValid()}
            >
              Submit Payment Plan Request
            </ButtonPrimary>
          )}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
